import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import { CheckMatyLoader } from "@/components/ui/CheckMatyLoader";
import { MfaChallenge } from "@/components/auth/MfaChallenge";
import { MfaEnrollmentScreen } from "@/components/auth/MfaEnrollmentScreen";
import { RegistrationPendingScreen } from "@/components/auth/RegistrationPendingScreen";

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: (
    "admin" | "tester" | "qualitaetsmanager" | "mangelbearbeiter" | "qm_admin" | "customer_approver"
  )[];
  excludedRoles?: (
    "admin" | "tester" | "qualitaetsmanager" | "mangelbearbeiter" | "qm_admin" | "customer_approver"
  )[];
  redirectTo?: string;
}

export function ProtectedRoute({
  children,
  requiredRoles,
  excludedRoles,
  redirectTo,
}: ProtectedRouteProps) {
  const {
    user,
    loading,
    roles,
    mfaRequired,
    mfaEnrollmentRequired,
    registrationPending,
    completeMfa,
  } = useAuth();
  const location = useLocation();

  if (loading) {
    return <CheckMatyLoader fullScreen />;
  }

  if (!user) {
    const currentUrl = `${window.location.origin}${location.pathname}${location.search}`;
    const authUrl = `/auth?redirect=${encodeURIComponent(currentUrl)}`;
    return <Navigate to={authUrl} replace />;
  }

  // MFA Gate: block app access until TOTP is verified
  if (mfaRequired) {
    return <MfaChallenge onVerified={completeMfa} />;
  }

  // MFA Enrollment Gate: force local users without MFA to enroll
  if (mfaEnrollmentRequired) {
    return <MfaEnrollmentScreen onComplete={completeMfa} />;
  }

  // Registration Approval Gate: block access until Super-Admin approves
  if (registrationPending) {
    return <RegistrationPendingScreen />;
  }

  // Check if user has an excluded role
  if (excludedRoles && excludedRoles.length > 0) {
    const hasExcludedRole = excludedRoles.some((role) => roles.includes(role));
    if (hasExcludedRole) {
      return <Navigate to={redirectTo || "/"} replace />;
    }
  }

  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some((role) => roles.includes(role));
    if (!hasRequiredRole) {
      return <Navigate to={redirectTo || "/"} replace />;
    }
  }

  return <>{children}</>;
}
